import { translate, type Lang } from './i18n'
import { humanize, policyDifferences } from './compare'
import type { HotspotBrief, SetChanges } from './api'

/**
 * En qué cambió un conjunto de hotspots respecto del anterior (§8.1).
 *
 * El mismo orden que la comparación de escenarios, y por la misma razón: **primero qué cambió en
 * la entrada y después qué resultó**. Un mapa distinto no dice nada hasta saber si cambió el
 * periodo, la ventana o el método; si nada de eso cambió y el mapa igual cambió, entonces la
 * noticia es el delito, y eso es lo que el analista vino a ver.
 *
 * Lo que decide es la **estabilidad**: si cada semana el mapa se rehace entero, el conjunto no
 * dice dónde patrullar. Va primero y con su lectura escrita al lado, porque un 38 % no significa
 * nada para quien abre esta pantalla por primera vez.
 */
export function SetChangesPanel({ lang, changes }: { lang: Lang; changes: SetChanges }) {
  const cambios = policyDifferences(changes.parametersBefore, changes.parametersAfter)
  const pct = (x: number) => `${Math.round(x)}%`

  // Los que se corrieron un puesto son ruido a sesenta hotspots; los que cruzaron media lista
  // cambiaron el turno de alguien.
  const movidos = changes.moved.filter((m) => Math.abs(m.rankDelta) >= 5)

  const punto = (h: HotspotBrief) => (
    <div key={h.hotspotId} className="card">
      <div className="row-line">
        <strong className="grow">#{h.rank} · {h.topOffense || '—'}</strong>
        <span className="chip">{Math.round(h.riskScore)}</span>
      </div>
      <div className="meta">
        {translate(lang, 'changes.point', [h.incidentCount, h.jurisdiction || '—'])}
      </div>
    </div>
  )

  return (
    <section className="panel">
      <h2>
        {translate(lang, 'changes.title', [
          changes.before.version, changes.after.version, changes.after.name,
        ])}
      </h2>

      {/*
        Cuando las retículas no coinciden, el emparejamiento es por cercanía y el resultado es
        aproximado. Callarlo sería peor que no comparar: el analista tomaría por movimiento del
        delito lo que es un desplazamiento del ancla.
      */}
      {!changes.sameGrid && (
        <p className="notice warning">
          {translate(lang, 'changes.differentGrid', [Math.round(changes.matchRadiusMeters)])}
        </p>
      )}

      <div className="card">
        <div className="row-line">
          <strong className="grow">{translate(lang, 'changes.stability')}</strong>
          <span className={`chip ${changes.stability >= 60 ? 'ok' : 'warning'}`}>
            {pct(changes.stability)}
          </span>
        </div>
        <p className="notice">{translate(lang, 'changes.stabilityHelp')}</p>
        <div className="meta">
          {translate(lang, 'changes.counts', [
            changes.shared, changes.entered, changes.left, pct(changes.persistence),
          ])}
        </div>
      </div>

      <table className="compare">
        <caption>{translate(lang, 'changes.inputs')}</caption>
        <tbody>
          <tr>
            <td>{translate(lang, 'changes.period')}</td>
            <td className="num">{changes.before.periodStart} → {changes.before.periodEnd}</td>
            <td className="num">{changes.after.periodStart} → {changes.after.periodEnd}</td>
          </tr>
          <tr>
            <td>{translate(lang, 'changes.incidents')}</td>
            <td className="num">{changes.before.inputCount}</td>
            <td className="num">{changes.after.inputCount}</td>
          </tr>
          <tr>
            <td>{translate(lang, 'changes.method')}</td>
            <td className="num">{changes.before.method} {changes.before.methodVersion}</td>
            <td className="num">{changes.after.method} {changes.after.methodVersion}</td>
          </tr>
          <tr>
            <td>{translate(lang, 'changes.window')}</td>
            <td className="num">{changes.before.timeWindow}</td>
            <td className="num">{changes.after.timeWindow}</td>
          </tr>
          {/* Y lo que difiera de los parámetros del motor, sin saber cuáles existen. */}
          {cambios.map((c) => (
            <tr key={c.key}>
              <td>{humanize(c.key.split('.').at(-1) ?? c.key)}</td>
              <td className="num">{c.a}</td>
              <td className="num">{c.b}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {movidos.length > 0 && (
        <>
          <h3>{translate(lang, 'changes.moved', [movidos.length])}</h3>
          <p className="notice">{translate(lang, 'changes.movedHelp')}</p>
          {movidos.slice(0, 10).map((m) => (
            <div key={m.after.hotspotId} className="card">
              <div className="row-line">
                <strong className="grow">{m.after.topOffense || '—'}</strong>
                <span className={`chip ${m.rankDelta < 0 ? 'ok' : 'warning'}`}>
                  #{m.before.rank} → #{m.after.rank}
                </span>
              </div>
              <div className="meta">
                {translate(lang, 'changes.riskDelta', [
                  Math.round(m.before.riskScore), Math.round(m.after.riskScore),
                ])}
              </div>
            </div>
          ))}
        </>
      )}

      {changes.new.length > 0 && (
        <>
          <h3>{translate(lang, 'changes.new', [changes.new.length])}</h3>
          <p className="notice">{translate(lang, 'changes.newHelp')}</p>
          {changes.new.slice(0, 10).map(punto)}
        </>
      )}

      {changes.dropped.length > 0 && (
        <>
          <h3>{translate(lang, 'changes.dropped', [changes.dropped.length])}</h3>
          <p className="notice">{translate(lang, 'changes.droppedHelp')}</p>
          {changes.dropped.slice(0, 10).map(punto)}
        </>
      )}

      {changes.entered === 0 && changes.left === 0 && movidos.length === 0 && (
        <p className="notice">{translate(lang, 'changes.same')}</p>
      )}
    </section>
  )
}
